import React, { useEffect, useState } from 'react'
import { brandCSS } from '../../brand'
import { HOME_CSS } from './home.css.js'
import Reveal from '../components/Reveal.jsx'
import HeroSection from './HeroSection.jsx'
import CruisesSection from './CruisesSection.jsx'
import ActivitiesSection from './ActivitiesSection.jsx'
import TestimonialsSection from './TestimonialsSection.jsx'
import CTASection from '../components/CTASection.jsx'
import BookingSearchForm from '../components/BookingSearchForm.jsx'
import { useRef } from 'react'
import { ArrowRight, MapPin, Clock, ChevronLeft, ChevronRight, Plus } from 'lucide-react'

const base = (window.wpData?.siteUrl ?? '').replace(/\/$/, '')

const HL          = window.wpData?.halongSection    ?? {}
const ITINERARIES = window.wpData?.homeItineraries  ?? []
const BLOG_POSTS  = window.wpData?.homePosts        ?? []
const FAQS        = Array.isArray(window.wpData?.faqs) ? window.wpData.faqs : Object.values(window.wpData?.faqs ?? {})

function PlaceholderImg({ bg, label }) {
  return (
    <svg width="100%" height="100%" viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: 'block' }}>
      <rect width="400" height="300" fill={bg} />
      <text x="200" y="158" fontFamily="'Cormorant Garamond', Georgia, serif" fontSize="18" fontWeight="300" letterSpacing="3" fill="rgba(255,255,255,0.28)" textAnchor="middle" dominantBaseline="middle">{label}</text>
    </svg>
  )
}

// ── Shared section header ──────────────────────────────────
function SectionHeader({ eyebrow, title, sub, light = false }) {
  return (
    <div>
      <div className="hp-eyebrow">{eyebrow}</div>
      <h2 className={`hp-title${light ? ' hp-title--light' : ''}`}>{title}</h2>
      <div className="hp-divider" />
      {sub && <p className={`hp-sub${light ? ' hp-sub--light' : ''}`}>{sub}</p>}
    </div>
  )
}

const HL_DEFAULTS = {
  eyebrow:   'The Destination',
  title:     'Ha Long Bay, Vietnam',
  body1:     'Designated a UNESCO World Heritage Site in 1994, Ha Long Bay is home to nearly 2,000 limestone karst islands rising from jade-green waters. Mist-shrouded at dawn, golden at dusk — this is one of the rare places on earth that exceeds its own reputation.',
  body2:     'LJN Cruise navigates these waters with unrivalled local expertise, taking you beyond the main routes into hidden lagoons, floating villages, and sea caves that few visitors ever see.',
  btn_label: 'Read the Travel Guide',
  btn_url:   `${base}/travel-guide`,
}

function HalongSection() {
  const eyebrow   = HL.eyebrow   || HL_DEFAULTS.eyebrow
  const title     = HL.title     || HL_DEFAULTS.title
  const body1     = HL.body1     || HL_DEFAULTS.body1
  const body2     = HL.body2     || HL_DEFAULTS.body2
  const btnLabel  = HL.btn_label || HL_DEFAULTS.btn_label
  const btnUrl    = HL.btn_url   || HL_DEFAULTS.btn_url
  const imgDesk   = HL.img_desktop || ''
  const imgMob    = HL.img_mobile  || ''

  return (
    <section className="hp-halong">
      {imgDesk
        ? <picture>
            {imgMob && <source media="(max-width: 768px)" srcSet={imgMob} />}
            <img src={imgDesk} alt="" className="hp-halong__img" />
          </picture>
        : <div className="hp-halong__img-fallback" />
      }
      <div className="hp-halong__overlay" />
      <div className="hp-halong__content">
        <div className="hp-inner" style={{ width: '100%' }}>
          <Reveal variant="fade-right">
            <div className="hp-halong__text">
              <SectionHeader eyebrow={eyebrow} title={title} light />
              <p className="hp-split__body">{body1}</p>
              <p className="hp-split__body" style={{ marginTop: -8, marginBottom: 32 }}>{body2}</p>
              <a href={btnUrl} className="ljn-btn-primary">{btnLabel} <ArrowRight size={14} strokeWidth={1.5} /></a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

function ItinerariesSection() {
  const scrollRef = useRef(null)

  const scroll = (dir) => {
    const el = scrollRef.current
    if (!el) return
    const card = el.querySelector('.hp-itin-card')
    const step = card ? card.offsetWidth + 24 : 360
    const maxScroll = el.scrollWidth - el.clientWidth
    const next = el.scrollLeft + dir * step
    if (next > maxScroll) {
      el.scrollTo({ left: 0, behavior: 'smooth' })
    } else if (next < 0) {
      el.scrollTo({ left: maxScroll, behavior: 'smooth' })
    } else {
      el.scrollBy({ left: dir * step, behavior: 'smooth' })
    }
  }

  if (!ITINERARIES.length) return null
  return (
    <section className="hp-section hp-section--white">
      <div className="hp-inner">
        <Reveal variant="fade-up">
          <div className="hp-itin__header">
            <SectionHeader eyebrow="Itineraries" title="Plan Your Voyage" sub="Carefully designed routes for every schedule and style of exploration." />
            <div className="hp-itin__nav">
              <button className="hp-itin__btn" onClick={() => scroll(-1)} aria-label="Previous">
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              <button className="hp-itin__btn" onClick={() => scroll(1)} aria-label="Next">
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </Reveal>
        <div className="hp-itin__carousel" ref={scrollRef}>
          {ITINERARIES.map((it) => (
            <a key={it.id} href={it.permalink} className="hp-itin-card">
              <div className="hp-itin-card__thumb">
                {it.thumb
                  ? <img src={it.thumb} alt={it.title} className="hp-itin-card__img" />
                  : <PlaceholderImg bg="#0B3250" label={it.title} />
                }
              </div>
              <div className="hp-itin-card__body">
                {it.cruise_name && (
                  <div className="hp-itin-card__cruise">{it.cruise_name}</div>
                )}
                <div className="hp-itin-card__name" dangerouslySetInnerHTML={{ __html: it.title }} />
                {it.excerpt && (
                  <p className="hp-itin-card__excerpt">{it.excerpt}</p>
                )}
                <div className="hp-itin-card__meta">
                  {it.duration && (
                    <span className="hp-itin-card__meta-item">
                      <Clock size={11} strokeWidth={1.5} />{it.duration}
                    </span>
                  )}
                  {it.departure && (
                    <span className="hp-itin-card__meta-item">
                      <MapPin size={11} strokeWidth={1.5} />{it.departure}
                    </span>
                  )}
                </div>
                <span className="hp-itin-card__arrow">View Itinerary <ArrowRight size={14} strokeWidth={1.5} /></span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}


function BlogSection() {
  const scrollRef = useRef(null)
  const autoRef   = useRef(null)
  const pauseRef  = useRef(false)

  const scrollTo = (left) => {
    const el = scrollRef.current
    if (!el) return
    const start     = el.scrollLeft
    const distance  = left - start
    const duration  = 680
    const startTime = performance.now()
    const ease = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t

    const step = (now) => {
      const elapsed  = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      el.scrollLeft  = start + distance * ease(progress)
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }

  const scroll = (dir) => {
    const el = scrollRef.current
    if (!el) return
    const card      = el.querySelector('.hp-blog-card')
    const step      = card ? card.offsetWidth + 28 : 360
    const maxScroll = el.scrollWidth - el.clientWidth
    const next      = el.scrollLeft + dir * step
    if (next > maxScroll) scrollTo(0)
    else if (next < 0)    scrollTo(maxScroll)
    else                  scrollTo(next)
  }

  React.useEffect(() => {
    autoRef.current = setInterval(() => {
      if (!pauseRef.current) scroll(1)
    }, 4000)
    return () => clearInterval(autoRef.current)
  }, [])

  if (!BLOG_POSTS.length) return null
  return (
    <section className="hp-section hp-section--white">
      <div className="hp-inner">
        <div className="hp-blog__header">
          <Reveal variant="fade-up">
            <SectionHeader eyebrow="Travel Guide" title="Stories From the Bay" />
          </Reveal>
          <div className="hp-blog__nav">
            <button className="hp-itin__btn" onClick={() => scroll(-1)} aria-label="Previous">
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button className="hp-itin__btn" onClick={() => scroll(1)} aria-label="Next">
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <div className="hp-blog__carousel"
          ref={scrollRef}
          onMouseEnter={() => { pauseRef.current = true }}
          onMouseLeave={() => { pauseRef.current = false }}
        >
          {BLOG_POSTS.map((p) => (
            <a key={p.id} href={p.link} className="hp-blog-card">
              <div className="hp-blog-card__thumb">
                {p.thumb
                  ? <img src={p.thumb} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <PlaceholderImg bg="#0B3250" label={p.title.slice(0, 28) + '…'} />
                }
              </div>
              <div className="hp-blog-card__body">
                {p.category && <div className="hp-blog-card__cat">{p.category}</div>}
                <div className="hp-blog-card__title" dangerouslySetInnerHTML={{ __html: p.title }} />
                {p.excerpt && <div className="hp-blog-card__excerpt">{p.excerpt}</div>}
                <div className="hp-blog-card__date">{p.date}</div>
              </div>
            </a>
          ))}
        </div>
        <div className="hp-blog__footer">
          <a href={`${base}/travel-guide`} className="ljn-btn-secondary">All Articles <ArrowRight size={14} strokeWidth={1.5} /></a>
        </div>
      </div>
    </section>
  )
}

function FAQSection() {
  const [open, setOpen] = useState(null)
  if (!FAQS.length) return null
  return (
    <section className="hp-faq-section">
      <div className="hp-inner hp-faq__inner">
        <div className="hp-faq__left">
          <Reveal variant="fade-up">
            <div className="hp-eyebrow">FAQ</div>
            <h2 className="hp-title">Frequently Asked Questions</h2>
            <div className="hp-divider" />
            <a href={`${base}/booking`} className="ljn-btn-primary">
              Book Now <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
        <div className="hp-faq__right">
          {FAQS.map((faq, i) => (
            <div key={i} className={`hp-faq-item${open === i ? ' hp-faq-item--open' : ''}`}>
              <button className="hp-faq-item__q" onClick={() => setOpen(open === i ? null : i)}>
                <span className="hp-faq-item__q-text">{faq.question}</span>
                <span className="hp-faq-item__q-icon"><Plus size={16} strokeWidth={1.5} /></span>
              </button>
              <div className="hp-faq-item__a">
                <p className="hp-faq-item__a-text">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Main ───────────────────────────────────────────────────
export default function HomePage({ data = {} }) {
  useEffect(() => {
    const root = document.getElementById('app-root')
    if (root) root.style.visibility = 'visible'
  }, [])
  
  return (
    <>
      <style>{brandCSS + HOME_CSS}</style>
      <HeroSection />
      <BookingSearchForm />
      <CruisesSection />
      <HalongSection />
      <ItinerariesSection />
      <ActivitiesSection />
      <TestimonialsSection />
      <BlogSection />
      <FAQSection />
      <CTASection contact={data?.contact ?? {}} />
    </>
  )
}
